import React, { FC } from 'react';

// Components
import { StatisticProgressBar } from './StatisticProgressBar';
import { StatisticHighlightedValue } from './StatisticHighlightedValue';

// Utils
import { classNames } from '@sportal365/smp-widget-utils';

// Constants
import {
  MINUTES_SIGN,
  PERCENTAGE_SIGN,
  STATISTICS_WITH_MINUTES_SIGN,
  STATISTICS_WITH_PERCENTAGE_SIGN,
} from '../../../../../../../packages/constants/h2hStatistics';

interface Props {
  name: string;
  valueOne: number;
  valueTwo: number;
  label: string;
  takeLowerValue?: boolean;
}

const getHighlightedValues = (valueOne: number, valueTwo: number, takeLowerValue?: boolean) => {
  const highlightFirstValue = takeLowerValue ? valueOne < valueTwo : valueOne > valueTwo;
  const highlightSecondValue = takeLowerValue ? valueTwo < valueOne : valueTwo > valueOne;

  return { highlightFirstValue, highlightSecondValue };
};

const getPercentageValues = (valueOne: number, valueTwo: number) => {
  const calculatedFullValue = valueOne + valueTwo;
  const percentageA = calculatedFullValue > 0 ? (valueOne / calculatedFullValue) * 100 : 0;
  const percentageB = calculatedFullValue > 0 ? (valueTwo / calculatedFullValue) * 100 : 0;

  return { percentageA, percentageB };
};

const getStatisticSign = (name: string) => {
  if (!name) return '';

  if (STATISTICS_WITH_PERCENTAGE_SIGN.includes(name)) {
    return PERCENTAGE_SIGN;
  }

  if (STATISTICS_WITH_MINUTES_SIGN.includes(name)) {
    return MINUTES_SIGN;
  }

  return '';
};

export const StatisticComparisonRow: FC<Props> = ({ name, valueOne, valueTwo, label, takeLowerValue }) => {
  const { highlightFirstValue, highlightSecondValue } = getHighlightedValues(valueOne, valueTwo, takeLowerValue);
  const { percentageA, percentageB } = getPercentageValues(valueOne, valueTwo);
  const sign = getStatisticSign(name);

  return (
    <div className={classNames({ 'pb-4 px-4': true }, `statistic-${name}-container`)}>
      <div className={classNames({ 'flex justify-between items-center mb-2': true }, `statistic-${name}-info-container`)}>
        <StatisticHighlightedValue
          value={`${valueOne}${sign}`}
          locator={`statistic-${name}-firstEntity-value`}
          isHighlighted={highlightFirstValue}
        />
        <span className={classNames({ 'text-4 text-hiContrast': true }, `statistic-${name}-label`)}>{label}</span>
        <StatisticHighlightedValue
          value={`${valueTwo}${sign}`}
          locator={`statistic-${name}-secondEntity-value`}
          isHighlighted={highlightSecondValue}
        />
      </div>
      <div
        className={classNames({ 'bg-progressContainerBgColor rounded-5 h-2 flex gap-1': true }, `statistic-${name}-progressBar-container`)}
      >
        <StatisticProgressBar
          percentValue={percentageA}
          position='end'
          backgroundColor='progressOneBgColor'
          locator={`statistic-${name}-firstEntity-progressBar`}
        />
        <StatisticProgressBar
          percentValue={percentageB}
          position='start'
          backgroundColor='progressTwoBgColor'
          locator={`statistic-${name}-secondEntity-progressBar`}
        />
      </div>
    </div>
  );
};
